import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, Input, OnDestroy, OnInit, signal, WritableSignal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MessageService } from "primeng/api";
import { catchError, distinctUntilChanged, EMPTY, map, of, Subject, switchMap, takeUntil } from "rxjs";
import { FormBuilderService } from "src/app/services/form-builder.service";
import { FieldLookupService } from "src/app/services/field-lookup.service";
import { FormField, FormModel, FormSection, FormState, ParagraphSegment } from "../form.types";
import { BreadcrumbService } from "xng-breadcrumb";
import { FormStateService } from "src/app/services/form-state.service";
import { safeSetItem } from "src/app/utils/safe-storage";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { ButtonModule } from "primeng/button";
import { TitleCasePipe } from "@angular/common";
import { FieldsV2Component } from "src/app/forms/fields-v2/fields-v2.component";
import { GlobalServicesService } from "src/app/services/global-services.service";
import { HttpErrorResponse } from "@angular/common/http";
import { OtpService } from "src/app/services/otp.service";
import { CrispService } from "src/app/services/crisp.service";
import { SharedModule } from "src/app/shared/shared.module";
import { EventServicesService } from "src/app/services/event-services.service";
import { RoleServicesService } from "src/app/services/role-services.service";
import { InnerHtmlViewComponent } from "src/app/shared/innerhtml.component";
import { GradientCssPipe } from "src/app/shared/gradient-picker/gradient-css.pipe";
import { SharedServicesService } from "src/app/services/shared-services.service";
import { JobServicesService } from "src/app/services/job-services.service";

interface JobRole {
    job_role_id: string;
    job_role_name: string;
    preferred_location: string[];
}

export interface FieldOptions {
    label: string,
    value: any
}

interface Field {
    id: number;
    name: string;
    type: string;
    label: string;
    description: string;
    order_no: number;
    is_required: boolean;
    placeholder: string | null;
    element_type: 'field' | 'group';
    options?: any[];
    form_fields?: Field[];
    properties?: FieldProperties;
}

export interface FieldProperties {
    file_format?: string[];
    file_max_size?: string;
}

@Component({
    selector: 'app-form-preview-v2',
    templateUrl: './form-preview-v2.component.html',
    styleUrls: ['./form-preview-v2.component.css'],
    imports: [ProgressSpinnerModule, ButtonModule, TitleCasePipe, FieldsV2Component, SharedModule, InnerHtmlViewComponent, GradientCssPipe],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class FormPreviewV2Component implements OnInit, OnDestroy {
    unsub$: Subject<void> = new Subject<void>();
    removeFiles$: Subject<void> = new Subject<void>();

    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private formBuilderService = inject(FormBuilderService);
    private fieldLookupService = inject(FieldLookupService);
    private messageService = inject(MessageService);
    private breadcrumbService = inject(BreadcrumbService);
    private formStateService = inject(FormStateService);
    private globalService = inject(GlobalServicesService);
    private otpService = inject(OtpService);
    private eventService = inject(EventServicesService);
    private roleService = inject(RoleServicesService);
    private sharedService = inject(SharedServicesService);
    private jobService = inject(JobServicesService);
    private crispService = inject(CrispService);

    editMode: WritableSignal<boolean> = signal<boolean>(false);
    formDirty: WritableSignal<boolean> = signal<boolean>(false);
    registrationId: WritableSignal<string> = signal<string>("");
    photoCaptureMode: string | null = null;

    @Input() previewMode: boolean = false;
    @Input() formData: FormState | null = null;

    uuid: WritableSignal<string> = signal<string>("");
    isLoading: WritableSignal<boolean> = signal<boolean>(false);
    errorType: WritableSignal<'not-found' | 'server-error' | 'invalid-uuid' | null> = signal(null);
    errorMessage: WritableSignal<string> = signal("");

    stateEmpty: WritableSignal<boolean> = signal<boolean>(false);
    lastUpdate: WritableSignal<number> = signal<number>(0);

    formType: WritableSignal<string> = signal("");
    formName: WritableSignal<string> = signal("");
    formLogo: WritableSignal<string> = signal("");
    formCoverImage: WritableSignal<string> = signal("");
    headerContent: WritableSignal<string> = signal("");
    templateCollections: WritableSignal<FormModel[][]> = signal([]);
    footerContent: WritableSignal<string> = signal("");
    markAsTouced: Subject<void> = new Subject<void>();
    verifyingEmail: boolean = false;
    
    loadingJobDetails: WritableSignal<boolean> = signal<boolean>(true);
    eventDetails: WritableSignal<any> = signal(null);
    showJobRoles: WritableSignal<boolean> = signal<boolean>(true);

    dependenciesFields: Set<string> = new Set();
    fieldsMap: Map<string, FormModel> = new Map();

    ngOnDestroy(): void {
        this.crispService.hideChat();
        this.unsub$.next();
        this.unsub$.complete();
    }

    showCrispBtn = false;
    private crispChatEnabled = false;

    ngOnInit(): void {
        this.route.data.pipe(takeUntil(this.unsub$)).subscribe((data) => {
            const preview = data["preview"];
            if (!preview && !this.previewMode) {
                this.initiateRegistrationForm();
            } else {
                this.previewMode = true;
                this.initiatePreviewForm();
            }
        })
    }

    private initCrisp(): void {
        this.crispService.load();
        this.showCrispBtn = this.crispService.isEnabled;
        this.crispService.chatOpened$.pipe(takeUntil(this.unsub$)).subscribe(() => this.showCrispBtn = false);
        this.crispService.chatClosed$.pipe(takeUntil(this.unsub$)).subscribe(() => this.showCrispBtn = true);
    }

    openCrispChat(): void {
        this.crispService.openChat();
    }


    initiatePreviewForm() {
        if (this.previewMode && this.formData) {
            this.loadFormData(this.formData);
            return;
        }

        this.route.queryParams.pipe(takeUntil(this.unsub$)).subscribe((params) => {
            const { uuid } = params;
            this.isLoading.set(true);
            this.uuid.set(uuid);

            if (uuid === "null" || !uuid) {
                this.subscribeToFormState();
            } else {
                this.loadDataFromApi();
            }
        })
    }

    updateBreadcrumb(): void {
        this.breadcrumbService.set('@formPreview', `${this.formName()} Preview`);
    }

    private createFieldModel(field: FormField, section: FormSection, groupName?: string): FormModel {
        const model: FormModel = {
            fieldId: field.id.toString(),
            fieldOrder: field.order_no,
            fieldName: field.label,
            fieldValue: field.name,
            fieldDescription: field.description,
            isMandatory: field.type.toLowerCase() === 'paragraph' ? false : field.is_required,
            formName: this.formName(),
            displaySection: section.section_label,
            placeHolder: field.placeholder || '',
            fieldType: field.type.toLowerCase(),
            fieldOptions: field.options || [],
            answer: [],
            isValid: this.previewMode || field.type.toLowerCase() === 'paragraph' ? true : !field.is_required,
            readOnly: this.previewMode ? true : false,
            visible: true,
            fileProperities: {
                file_format: field.properties?.file_format || [],
                file_max_size: field.properties?.file_max_size || '2MB'
            },
            dependencies: field.dependencies,
            is_required: field.is_required,
            element_type: (groupName && !(field.name === 'primary_number' || field.name === 'mobile_number' || field.name === 'secondary_number' || field.name === 'parent_number')) ? "group" : "field",
            section_label: field.section_label
        };

        if (!this.previewMode && !this.editMode() && (groupName === 'email' || groupName === 'Email') && field.name === 'email_id' && this.candidateEmail()) {
            model.answer = [this.candidateEmail()];
            model.disabled = true;
            model.readOnly = true;
            model.isValid = true;
        }

        if (groupName) {
            model.groupName = groupName;
        }

        this.setFieldOptions(field, model);

        if (this.previousFormDetails().hasOwnProperty(model.fieldValue) && this.previousFormDetails()[model.fieldValue] !== null && this.previousFormDetails()[model.fieldValue] !== undefined) {
            if (model.fieldType === 'date picker' && this.previousFormDetails()[model.fieldValue]) {
                const dateValue = new Date(this.previousFormDetails()[model.fieldValue]);
                const formattedDate = `${('0' + (dateValue.getMonth() + 1)).slice(-2)}-${('0' + dateValue.getDate()).slice(-2)}-${dateValue.getFullYear()}`;
                model.answer = [formattedDate]
                // model.placeHolder = formattedDate
            } else if (model.fieldType === 'checkbox') {
                const prevValue = this.previousFormDetails()[model.fieldValue];
                if (Array.isArray(prevValue)) {
                    model.answer = [...prevValue];
                } else if (prevValue) {
                    model.answer = [prevValue];
                } else {
                    model.answer = [];
                }
            } else if (model.fieldType === 'dropdown') {
                const selectedOption = model.fieldOptions.find(option => option.value === this.previousFormDetails()[model.fieldValue]);
                if (selectedOption) {
                    model.answer = [selectedOption];
                    // model.placeHolder = selectedOption.label;
                } else {
                    model.answer = [];
                    // model.placeHolder = '';
                }
            } else if (["primary_number", "mobile_number", "parent_number", "mobile"].includes(model.fieldType)) {
                let answer = (this.previousFormDetails()[model.fieldValue] || "") as string;
                model.answer = [];
                // model.placeHolder = "";
                
                if(model.fieldValue === "secondary_number") {
                    const secondaryCountryCode = this.previousFormDetails()["secondary_num_country_code"];
                    if(secondaryCountryCode) {
                        answer = secondaryCountryCode + "-" + answer
                    }
                } 

                if(model.fieldValue === "mobile_number") {
                    const countryCode = this.previousFormDetails()["country_code"];
                    if(countryCode) {
                        answer = countryCode + "-" + answer
                    }
                }

                if (answer.includes("-")) {
                    const splitNumber = answer.split("-");
                    if (splitNumber.length && splitNumber.length === 2) {
                        model.answer = [splitNumber[0], splitNumber[1]]
                        // model.placeHolder = model.answer[1]
                    } else if(splitNumber.length === 3) {
                        model.answer = [`${splitNumber[0]}-${splitNumber[1]}`, splitNumber[2]]
                        // model.placeHolder = splitNumber[2]
                    }
                } else {
                    model.answer = [answer ? this.DEFAULT_COUNTRY_CODE : null, answer];
                    // model.placeHolder = model.answer[1];
                }
            } else {
                model.answer = Array.isArray(this.previousFormDetails()[model.fieldValue])
                    ? this.previousFormDetails()[model.fieldValue]
                    : [this.previousFormDetails()[model.fieldValue]];
                model.placeHolder = model.answer[0]
            }

            if (model.isMandatory && model.answer?.length) {
                model.isValid = true;
            }
            if (this.editMode() && model.fieldType === 'email') {
                if (model.fieldValue === 'email_id' && model.answer?.[0]) {
                    this.emailValue.set(model.answer[0]);
                }
                if (model.fieldValue === 'confirm_email_id' && model.answer?.[0]) {
                    this.emailConfirmValue.set(model.answer[0]);
                }
                if (this.emailValue() && this.emailConfirmValue() && this.emailValue() === this.emailConfirmValue()) {
                    this.duplicateData.emailDuplicate = true;
                }
            }

            if (this.editMode() && this.othersInputFields.includes(model.fieldValue)) {
                const storedValue = this.previousFormDetails()[model.fieldValue];
                const hasOthersOption = (model.fieldOptions || []).some(
                    (option: any) => String(option?.value ?? '').toLowerCase() === 'others');
                if ((!model.answer || !model.answer.length) && hasOthersOption
                    && typeof storedValue === 'string' && storedValue.trim()) {
                    model.answer = [{ label: 'Others', value: 'Others' }];
                    this.otherValueData[model.fieldValue] = storedValue.trim();
                    model.isValid = this.isValidOtherValue(model.fieldValue);
                }
            }

            if (this.editMode() && ["pg_college_name", "ug_college_name", "diploma_college_name"].includes(model.fieldValue)) {
                const storedValue = this.previousFormDetails()[model.fieldValue];
                if ((!model.answer || !model.answer.length) && typeof storedValue === 'string' && storedValue.includes(',')) {
                    const parts = storedValue.split(',').map((p: string) => p.trim());
                    if (parts.length >= 3) {
                        model.answer = [{ label: 'Others', value: 'Others' }];
                        this.otherCollegeData[`${model.fieldValue}_college_name`] = parts[0];
                        this.otherCollegeData[`${model.fieldValue}_college_city`] = parts[1];
                        this.otherCollegeData[`${model.fieldValue}_college_state`] = parts.slice(2).join(', ');
                        model.isValid = true;
                    }
                }
            }
        }

        return model;
    }

    private setFieldOptions(field: Field, model: FormModel) {
        const staticOptions = field.options || [];
        
        switch (field.name) {
            case 'preferred_role':
                model.fieldOptions = this.jobRoles().length > 0 ? this.jobRoles() : staticOptions;
                break;
            case 'preferred_location_1':
            case 'preferred_location_2':
            case 'preferred_location_3':
                model.fieldOptions = [...staticOptions, ...this.preferredLocations()];
                break;
            case 'pg_college_name':
            case 'ug_college_name':
            case 'diploma_college_name':
                model.fieldOptions = [...staticOptions, ...this.getInstituteOptions()];
                break;
            case 'certification_1':
                model.fieldOptions = [...staticOptions, ...this.getCertificationOptions()];
                break;
            case 'known_languages':
                model.fieldOptions = [...staticOptions, ...this.getLanguageOptions()];
                break;
            case 'skill_list':
                model.fieldOptions = [...staticOptions, ...this.getSkillOptions()];
                break;
            case 'class10_board_name':
                model.fieldOptions = [...staticOptions, ...this.getSscBoardOptions()];
                break;
            case 'class12_board_name':
                model.fieldOptions = [...staticOptions, ...this.getIntermediateBoardOptions()];
                break;
            case 'entrance_test':
                model.fieldOptions = [...staticOptions, ...this.getEntranceTestOptions()];
                break;
            case 'pg_degree':
            case 'ug_degree':
            case 'diploma_degree':
                model.fieldOptions = this.mergeMasterOptions(staticOptions, this.getEducationDegreeOptions());
                break;
            case 'pg_specialization':
            case 'ug_specialization':
            case 'diploma_specialization':
                model.fieldOptions = this.mergeMasterOptions(staticOptions, this.getEducationBranchOptions());
                break;
            default:
                model.fieldOptions = staticOptions;
                break;
        }
    }

    private getInstituteOptions(): any[] {
        return (this.instituteList()?.items || [])?.length > 0 
            ? this.formBuilderService.mapFieldOptions('institute', this.instituteList()?.items, ['name', 'city', 'state'], ['name', 'city', 'state'])
            : [];
    }

    private getCertificationOptions(): any[] {
        return this.certificateList().length > 0 
            ? this.formBuilderService.mapFieldOptions('certification', this.certificateList(), ['name'], 'name')
            : [];
    }

    private getLanguageOptions(): any[] {
        return this.languagesList().length > 0 
            ? this.formBuilderService.mapFieldOptions('language', this.languagesList(), ['name'], 'name')
            : [];
    }

    private getSkillOptions(): any[] {
        return this.skillsList().length > 0 
            ? this.formBuilderService.mapFieldOptions('skill', this.skillsList(), ['name'], 'name')
            : [];
    }

    private getSscBoardOptions(): any[] {
        return this.sscBoardsList().length > 0 
            ? this.formBuilderService.mapFieldOptions('ssc_board', this.sscBoardsList(), ['name'], 'name')
            : [];
    }

    private getIntermediateBoardOptions(): any[] {
        return this.intermediateBoardsList().length > 0 
            ? this.formBuilderService.mapFieldOptions('intermediate_board', this.intermediateBoardsList(), ['name'], 'name')
            : [];
    }

    private getEducationDegreeOptions(): any[] {
        return this.educationDegreesList().length > 0
            ? this.formBuilderService.mapFieldOptions('education_degree', this.educationDegreesList(), ['name'], 'name')
            : [];
    }

    private getEducationBranchOptions(): any[] {
        return this.educationBranchesList().length > 0
            ? this.formBuilderService.mapFieldOptions('education_branch', this.educationBranchesList(), ['name'], 'name')
            : [];
    }

    /**
     * Keeps options a drive already had and fills the rest from master data, "Others" last.
     * Matching ignores case and separators: older forms store "computer_science" where master
     * data stores "Computer Science", and without that they would both be listed.
     */
    private mergeMasterOptions(staticOptions: any[], masterOptions: any[]): any[] {
        const normalise = (value: any) => String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const seen = new Set<string>();
        const merged: any[] = [];

        const masterByKey = new Map<string, any>(
            masterOptions.map((option: any) => [normalise(option?.label) || normalise(option?.value), option]));

        [...(staticOptions || []), ...masterOptions].forEach((option: any) => {
            const key = normalise(option?.label) || normalise(option?.value);
            if (!key || seen.has(key)) {
                return;
            }
            seen.add(key);
            const master = masterByKey.get(key);
            merged.push(master ? { ...option, label: master.label } : option);
        });

        const isOthers = (option: any) => String(option?.value ?? '').toLowerCase() === 'others';
        const others = merged.filter(isOthers);
        return others.length ? [...merged.filter(option => !isOthers(option)), ...others] : merged;
    }

    private getEntranceTestOptions(): any[] {
        return this.entranceTestsList().length > 0 
            ? this.formBuilderService.mapFieldOptions('entrance_test', this.entranceTestsList(), ['name'], 'name')
            : [];
    }

    transformFormFields(data: FormState | null) {
        this.templateCollections.set([])
        this.formType.set(data?.form_type || "");
        this.formName.set(data?.name || "");
        this.formLogo.set(data?.logo || "");
        this.formCoverImage.set(data?.cover_image || "");
        this.headerContent.set(data?.header_content || "");
        this.footerContent.set(data?.footer_content || "");

        this.fieldRemoved = [];
        this.formData?.form_fields.forEach((section: FormSection) => {
            const sectionFields: FormModel[] = [];

            section.section_form_fields.forEach((field: FormField) => {
                if (field.element_type === "field") {
                    sectionFields.push(this.createFieldModel(field, section));
                    return;
                }

                if (field.element_type === "group" && field.form_fields) {
                    let groupFields = field.form_fields;

                    if (["Primary number", "Secondary Num", "parent_number", "primary_number", "secondary_num"].includes(field.name)) {
                        if (groupFields.some((f) => f.name === "country_code")) {
                            this.fieldRemoved.push("country_code")
                        }
                        if (groupFields.some(f => f.name === "secondary_num_country_code")) {
                            this.fieldRemoved.push("secondary_num_country_code")
                        }
                        groupFields = groupFields.filter(f => f.name !== 'country_code' && f.name !== 'secondary_num_country_code');
                    }

                    groupFields.forEach((groupField: FormField) => {
                        sectionFields.push(this.createFieldModel(groupField, section, field.name));
                    });
                }
            });

            if (sectionFields.length) {
                this.templateCollections.update((templateCollection) => [...templateCollection, sectionFields])
            }
        })
        this.updateDependentField();

        this.updateBreadcrumb();

        if (Object.keys(this.previousFormDetails()).length > 0) {
            this.reEvaluateDependenciesAfterPrefill();
        }

        this.resolveParagraphTokens();
    }

    private reEvaluateDependenciesAfterPrefill() {
        this.templateCollections()?.forEach((section) => {
            section?.forEach((field) => {
                // Only re-evaluate fields that have answers and have dependencies defined
                if (field.answer && field.answer.length && field.dependencies && field.dependencies.length) {
                    this.checkCanShowDependentField(field);
                }
            });
        });
    }

    loadFormData(data: FormState | null) {
        if (data) {
            this.formData = data;
            this.transformFormFields(data);
        }

        this.isLoading.set(false);
    }

    loadDataFromApi() {
        this.formBuilderService.getFormFastApi(this.uuid()).pipe(takeUntil(this.unsub$)).subscribe({
            next: (res) => {
                const { body } = res;
                if (body) {
                    this.loadFormData(body);
                    return;
                }

                this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Invalid form state' });
            },
            error: (err) => {
                this.isLoading.set(false);
                this.handleApiError(err);
            },
            complete: () => {
                this.isLoading.set(false);
            }
        })
    }

    subscribeToFormState() {
        this.formStateService.getFormState().pipe(
            takeUntil(this.unsub$), distinctUntilChanged((prev, curr) =>
                JSON.stringify(prev) === JSON.stringify(curr)
            )).subscribe({
                next: (state) => {
                    if (!state || !state.form_fields || state.form_fields.length === 0) {
                        this.stateEmpty.set(true);
                        this.formData = null;
                    } else if (state.lastUpdated && state.lastUpdated > this.lastUpdate()) {
                        this.stateEmpty.set(false);
                        this.lastUpdate.set(state.lastUpdated);
                        this.loadFormData(state);
                    }

                    this.isLoading.set(false);
                },
                error: (err) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load form preview' });
                    this.isLoading.set(false);
                },
            });
    }

    handleApiError(error: any) {
        if (error.status === 404) {
            this.errorType.set('not-found');
            this.errorMessage.set('Form not found. The form you are looking for does not exist.');
        } else if (error.status === 400) {
            this.errorType.set('invalid-uuid');
            this.errorMessage.set('Invalid form ID provided.');
        } else {
            this.errorType.set('server-error');
            this.errorMessage.set('Internal server error. Please try again later.');
        }

        this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.errorMessage()
        });
    }

    backToEdit() {
        this.router.navigate(['/forms/create']);
    }

    backToForms() {
        this.router.navigate(['/forms']);
    }

    // Registration Form
    duplicateData: any = {};
    fieldRemoved: string[] = [];
    readonly DEFAULT_COUNTRY_CODE = "91";

    submittingForm: WritableSignal<boolean> = signal<boolean>(false);
    offerFormSuccess: WritableSignal<boolean> = signal<boolean>(false);
    submittedSuccessfully: WritableSignal<boolean> = signal<boolean>(false);
    invalidUrl: WritableSignal<boolean> = signal<boolean>(false);
    formExpired: WritableSignal<boolean> = signal<boolean>(false);
    registrationNotStarted: WritableSignal<boolean> = signal<boolean>(false);
    offerFormWarning: WritableSignal<boolean> = signal<boolean>(false);
    documentationError: WritableSignal<boolean> = signal<boolean>(false);
    documentationErrorMsg: WritableSignal<string> = signal<string>("")
    alreadyRegistered: WritableSignal<boolean> = signal<boolean>(false);

    companyUUID: WritableSignal<string> = signal<string>("");
    sourceId: WritableSignal<string> = signal<string>("");
    eventId: WritableSignal<string> = signal<string>("");
    formId: WritableSignal<string> = signal<string>("");
    candidateUUID: WritableSignal<string> = signal<string>("");
    verifyByEmail: WritableSignal<boolean> = signal<boolean>(false);

    candidateEmail: WritableSignal<string> = signal<string>("");
    stageId: WritableSignal<string> = signal<string>("");
    otpRequired: WritableSignal<boolean> = signal<boolean>(false);
    otpVerified: WritableSignal<boolean> = signal<boolean>(false);

    emailValue: WritableSignal<string> = signal<string>("");
    emailConfirmValue: WritableSignal<string> = signal<string>("");
    emailToVerify: WritableSignal<string> = signal<string>("");
    verifyChoice: WritableSignal<string> = signal<string>("");
    emailVerified: WritableSignal<boolean> = signal<boolean>(false);

    enableMobile: WritableSignal<boolean> = signal<boolean>(false);
    mobileVerified: WritableSignal<boolean> = signal<boolean>(false);

    preferredLocations: WritableSignal<any[]> = signal([])
    jobRoles: WritableSignal<any[]> = signal([]);
    instituteList: WritableSignal<any> = signal({});
    certificateList: WritableSignal<any[]> = signal([]);
    languagesList: WritableSignal<any[]> = signal([]);
    skillsList: WritableSignal<any[]> = signal([]);
    sscBoardsList: WritableSignal<any[]> = signal([]);
    intermediateBoardsList: WritableSignal<any[]> = signal([]);
    entranceTestsList: WritableSignal<any[]> = signal([]);
    educationDegreesList: WritableSignal<any[]> = signal([]);
    educationBranchesList: WritableSignal<any[]> = signal([]);

    verifyingOTP: WritableSignal<boolean> = signal<boolean>(false);
    otpSent: WritableSignal<boolean> = signal<boolean>(false);
    otpTimer: WritableSignal<number> = signal<number>(60);
    otpCountdown: WritableSignal<string> = signal<string>("");
    otpIntervalId: any = null;
    previousFormDetails: WritableSignal<any> = signal<any>({})
    enableVerify: WritableSignal<boolean> = signal<boolean>(false);
    otpValue: any;
    otpDigits: string[] = ['', '', '', '', '', ''];
    otpInputs = Array(6).fill(0);
    loadResend: boolean = false;

    // Skill scoring properties
    companyMetadata: WritableSignal<any> = signal<any>({});
    skillScoreEnabled: WritableSignal<boolean> = signal<boolean>(false);

    initiateRegistrationForm() {
        this.previewMode = false;
        this.invalidUrl.set(false);

        this.route.paramMap.pipe(takeUntil(this.unsub$)).subscribe(params => {
            this.formType.set(params.get('form_type') ? params.get('form_type')!.toLowerCase() : '');
            if (!['registration', 'documentation', 'offer'].includes(this.formType())) {
                this.invalidUrl.set(true);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Invalid URL' });
            }

            if (this.formType() === 'registration') {
                this.formName.set("Registration Form");
            } else if (this.formType() === 'documentation') {
                this.formName.set('Extended Details and Documentation Form');
            }
        });

        this.sharedService.initiateMetadataRequest(() => this.globalService.metadataFastApi()).pipe(
            takeUntil(this.unsub$),
            map((result: any) => result?.body?.company_details ?? result),
            switchMap((companyDetails: any) => {
                if (companyDetails) {
                    this.companyUUID.set(companyDetails.uuid);
                    this.companyMetadata.set(companyDetails);
                    this.skillScoreEnabled.set(this.checkSkillScoreEnabled());
                    safeSetItem('companyUUID', this.companyUUID());
                    this.crispChatEnabled = !!companyDetails.company_config?.crisp_chat;
                    return this.route.queryParamMap;
                }
                return of(null);
            }),
        ).subscribe({
            next: (param) => {
                if (!param) {
                    return;
                }

                this.sourceId.set(param.get("source") || "");
                this.eventId.set(param.get('eventId') || '');
                this.formId.set(param.get('formId') || '');
                this.candidateUUID.set(param.get('candidateId') || '');
                this.editMode.set(param.get('editMode') === 'true');
                this.registrationId.set(param.get('registrationId') || '');
                this.verifyByEmail.set(!this.editMode());
                if (this.editMode()) {
                    this.showJobRoles.set(false);
                }
                if (this.crispChatEnabled && !this.editMode()) {
                    this.initCrisp();
                }

                // Load form details immediately only for registration
                if (this.formType() === 'registration') {
                    if (this.candidateUUID() && !this.editMode()) {
                        this.formBuilderService.validateCandidateFastApi(this.eventId(), this.candidateUUID(), this.formId(), 'registration')
                            .pipe(takeUntil(this.unsub$))
                            .subscribe({
                                next: (candidateResponse: any) => {
                                    const candidateEmail = candidateResponse?.body?.candidate_email;
                                    if (candidateEmail) {
                                        this.candidateEmail.set(candidateEmail);
                                        this.emailValue.set(candidateEmail);
                                        if (this.formData) {
                                            this.transformFormFields(this.formData);
                                        }
                                    }
                                },
                                error: () => {
                                    // Fall back to manual email entry + verification if the candidate email can't be resolved.
                                }
                            });
                    }
                    this.formBuilderService.getEventFormDetailsFastApi(this.eventId(), this.formId(), this.editMode()).pipe(takeUntil(this.unsub$)).subscribe({
                        next: (formResponse: any) => {
                            this.formData = formResponse.body;
                            if (this.formData) {
                                this.formLogo.set(this.formData.logo ?? "");
                                this.formCoverImage.set(this.formData.cover_image ?? "");
                                this.headerContent.set(this.formData.header_content ?? "");
                                this.footerContent.set(this.formData.footer_content ?? "");
                                this.transformFormFields(this.formData);
                                if (!this.editMode()) {
                                    this.isLoading.set(false);
                                }
                            }
                            this.loadLookupDataInBackground();

                            if (this.editMode() && this.candidateUUID()) {
                                this.emailVerified.set(true);
                                this.otpVerified.set(true);
                                this.jobService.getCandidateInfo(this.candidateUUID(), this.eventId(), true)
                                    .pipe(takeUntil(this.unsub$))
                                    .subscribe({
                                        next: (response: any) => {
                                            const candidateDetails = response?.body?.candidate_details;
                                            if (candidateDetails) {
                                                this.previousFormDetails.set(candidateDetails);
                                                this.transformFormFields(this.formData);
                                            }
                                            this.isLoading.set(false);
                                        },
                                        error: (err: any) => {
                                            this.messageService.add({
                                                severity: 'error',
                                                summary: 'Error',
                                                detail: 'Failed to load candidate details for editing.'
                                            });
                                            this.isLoading.set(false);
                                        }
                                    });
                            }
                        },
                        error: (error) => {
                            this.isLoading.set(false);
                            this.formExpired.set(false)
                            this.invalidUrl.set(false);
                            this.registrationNotStarted.set(false);

                            if (error.status === 500) {
                                this.invalidUrl.set(true);
                                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Something went wrong. Please try again later.' });
                            }
                            else if (error.status === 404) {
                                this.invalidUrl.set(true);
                                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'The selected event is invalid.' });
                            }
                            else if (error.status === 400) {
                                switch (error.error?.detail) {
                                    case "Registration was closed":
                                        this.formExpired.set(true);
                                        break;
                                    case "Registration not started yet":
                                        this.registrationNotStarted.set(true);
                                        break;
                                    default:
                                        this.invalidUrl.set(true);
                                }
                            }
                        }
                    });
                }
    
                // For documentation and offer forms, only validate candidate and setup OTP page, defer form loading until OTP verified
                if (this.formType() === 'documentation' || this.formType() === 'offer') {
                    this.formBuilderService.validateCandidateFastApi(this.eventId(), this.candidateUUID(), (this.formType() === 'documentation' || this.formType() === 'offer') ? this.formId() : null, this.formType())
                        .pipe(
                            catchError((error: HttpErrorResponse) => {
                                if (error.status === 400 && this.formType() === 'documentation') {
                                    switch (error.error?.detail) {
                                        case "documentation submission deadline has expired":
                                        case "Candidate has already submitted the documentation":
                                            this.documentationError.set(true);
                                            this.documentationErrorMsg.set(error.error.detail);
                                            break;
                                        default:
                                            this.invalidUrl.set(true);
                                            break;
                                    }
                                } else if (error.status === 400 && this.formType() === 'offer') {
                                    this.offerFormWarning.set(true);
                                    switch (error.error?.detail) {
                                        case "offer submission deadline has expired":
                                        case "Candidate has already submitted the offer":
                                            this.documentationErrorMsg.set(error.error.detail);
                                            break;
                                        default:
                                            this.invalidUrl.set(true);
                                            break;
                                    }
                                } else {
                                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to validate candidate, Check the URL' });
                                    this.invalidUrl.set(true);
                                }
                                this.isLoading.set(true);
                                return EMPTY;
                            })
                        ).subscribe((candidateResponse: any) => {
                            if (candidateResponse?.body?.candidate_email) {
                                this.candidateEmail.set(candidateResponse.body.candidate_email);
                                this.stageId.set(candidateResponse.body.event_stage_id);
                                this.otpRequired.set(true);
                                this.otpVerified.set(false);
                                this.emailToVerify.set(candidateResponse.body.candidate_email);
                                this.emailValue.set(candidateResponse.body.candidate_email);
                                this.crispService.setUserIdentity(candidateResponse.body.candidate_email);
                            }
                        });
                }
    
            },
            error: (error) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to initialize form' });
            }
        });
    }

    toggleActiveJobRole(jobRole: any) {
        const eventDetails = this.eventDetails()
        if (!eventDetails) {
            return;
        }

        if (eventDetails?.job_roles?.length === 1) {
            return;
        }

        (eventDetails?.job_roles as any[])?.forEach((role) => {
            if (role.job_role_id === jobRole.job_role_id) {
                role.isOpened = !role.isOpened;
            }
        })
    }

    toggleJobDetailsAndForm(val: boolean) {
        this.showJobRoles.set(val);
        window.scrollTo({ top: 0 });
    }

    get formHasPreferredRole(): boolean {
        return this.templateCollections()?.some(template =>
            template?.some(field => field.fieldValue === 'preferred_role')
        ) ?? false;
    }

    selectTheRole(role: any) {
        if (!role.job_role_id) {
            return;
        }
        this.templateCollections()?.forEach(template => {
            const preferred_role = template?.find(field => field.fieldValue === 'preferred_role')
            if (preferred_role) {
                preferred_role.answer = [role.job_role_id];
                if (preferred_role.isMandatory) {
                    preferred_role.isValid = true;
                }
            }
        })
        this.toggleJobDetailsAndForm(false);
    }

    enableEmailVerification(event: any) {
        this.verifyChoice.set("email")
        this.emailToVerify.set(event.email);
        this.crispService.setUserIdentity(event.email);
        this.sendOtpByEmail();
    }

    sendOtpByEmail = () => {
        this.verifyingEmail = true;
        const payload = {
            "email": this.emailToVerify().toLowerCase(),
        }
        const origin = this.formType() === 'offer' ? 'offer' : this.formType() === 'documentation' ? 'documentation' : 'registration';
        this.otpService.sendCandidateOtpFastApi(payload, this.eventId(), origin, this.sourceId()).subscribe(res => {
            if (res.body.detail == 'OTP sent successfully.') {
                this.otpSent.set(true);
                this.loadResend = false;
                this.startOtpTimer();
            } else {
                this.otpSent.set(false);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Something Went Wrong!' });
            }
            this.verifyingEmail = false;
        }, (err) => {
            this.verifyingEmail = false;
            if (err.status === 409 || err.error?.detail === "Candidate is already registered for this event.") {
                this.alreadyRegistered.set(true);
            }
        })
    }

    startOtpTimer() {
        this.otpTimer.set(60);
        this.otpCountdown.set(`${this.otpTimer().toString().padStart(2, '0')}`);

        this.otpIntervalId = setInterval(() => {
            this.otpTimer.update((val) => {
                const updatedVal = val - 1;
                return updatedVal;
            })
            this.otpCountdown.set(`${this.otpTimer().toString().padStart(2, '0')}`);

            if (this.otpTimer() === 0) {
                clearInterval(this.otpIntervalId);
            }
        }, 1000);
    }

    resendEmailOtp = () => {
        this.loadResend = true;
        this.sendOtpByEmail();
    }

    onOtpPaste(event: ClipboardEvent) {
        event.preventDefault();
        const pastedData = event.clipboardData?.getData('text') || '';
        const numbersOnly = pastedData.replace(/\D/g, '');
        if (numbersOnly.length === 6) {
            for (let i = 0; i < 6; i++) {
                this.otpDigits[i] = numbersOnly[i];
            }
            this.otpValue = this.otpDigits.join('');
            this.verifyCheck(this.otpValue);
            setTimeout(() => {
                const otpInputs = document.querySelectorAll('.otp-input') as NodeListOf<HTMLInputElement>;
                if (otpInputs[5]) otpInputs[5].focus();
            }, 0);
        }
    }

    onOtpInput(event: any, index: number) {
        const input = event.target;
        const value = input.value;

        if (value && index < 5) {
            const next = input.nextElementSibling;
            if (next) next.focus();
        }

        this.otpValue = this.otpDigits.join('');
        this.verifyCheck(this.otpValue);
    }

    onOtpKeyDown(event: any, index: number) {
        const input = event.target;
        if (event.key === 'Backspace' && !input.value && index > 0) {
            const prev = input.previousElementSibling;
            if (prev) prev.focus();
        }
    }

    verifyCheck(event: any) {
        if (this.otpValue.length == 6 && !isNaN(Number(event))) {
            this.enableVerify.set(true)
        } else {
            this.enableVerify.set(false);
        }
    }

    verifyEmailOtp() {
        if (this.otpValue.toString().length == 6 && this.enableVerify()) {
            this.verifyingOTP.set(true);
            let payload = { email: this.emailToVerify().toLowerCase(), otp: this.otpValue };
            this.otpService.verifyCandidateOtpFastApi(payload, this.eventId()).subscribe({
                next: (res) => {
                    if (res.status == 200) {
                        if (res.body.access_token) {
                            let accessToken = {
                                access_token: res.body.access_token,
                                token_type: res.body.token_type
                            }
                            safeSetItem('token', JSON.stringify(accessToken));
                            this.messageService.add({ severity: 'success', summary: 'Verified', detail: res.body.detail });
                            this.emailVerified.set(true);
                            this.otpVerified.set(true);
                            this.dialogClose();
                            if (res?.body?.previous_form_details) {
                                this.previousFormDetails.set(res.body.previous_form_details);
                                this.transformFormFields(this.formData)
                            }
                        } else {
                            this.messageService.add({ severity: 'error', summary: 'Expired', detail: res.body.detail });
                        }
                    }
                    this.verifyingOTP.set(false);
                },
                error: (error) => {
                    this.otpValue = '';
                    this.otpDigits = ['', '', '', '', '', ''];
                    this.emailVerified.set(false);
                    this.verifyingOTP.set(false);
                    if (error?.body?.detail) {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: error.body.detail });
                    }
                }
            });
        }
    }

    dialogClose() {
        this.otpSent.set(false);
        this.otpTimer.set(60);
        clearInterval(this.otpIntervalId)
    }

    prefillForm() {
        if ((this.otpVerified() || this.emailVerified()) && (this.formType() === 'documentation' || this.formType() === 'offer')) {
            this.getTemplates();
        }
    }

    getTemplates = () => {
        this.isLoading.set(true);
        this.formBuilderService.getEventFormDetailsFastApi(this.eventId(), this.formId()).subscribe({
            next: (res: any) => {
                this.formData = res.body;
                if (this.formData) {
                    this.formLogo.set(this.formData.logo ?? "");
                    this.formCoverImage.set(this.formData.cover_image ?? "");
                    this.headerContent.set(this.formData.header_content ?? "");
                    this.footerContent.set(this.formData.footer_content ?? "");
                    this.transformFormFields(this.formData);
                }
                this.isLoading.set(false);
            },
            error: (error: HttpErrorResponse) => {
                this.isLoading.set(false);
                this.formExpired.set(false)
                this.invalidUrl.set(false);
                this.registrationNotStarted.set(false);

                if (error.status === 500) {
                    this.invalidUrl.set(true);
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Something went wrong. Please try again later.' });
                }
                else if (error.status === 404) {
                    this.invalidUrl.set(true);
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'The selected event is invalid.' });
                }
                else if (error.status === 400) {
                    switch (error.error?.detail) {
                        case "Registration was closed":
                            this.formExpired.set(true);
                            break;
                        case "Registration not started yet":
                            this.registrationNotStarted.set(true);
                            break;
                        default:
                            this.invalidUrl.set(true);
                    }
                }
            }
        });
    }

    otherCollegeData: any = {
        pg_college_name_college_name: "",
        pg_college_name_college_city: "",
        pg_college_name_college_state: "",
        ug_college_name_college_name: "",
        ug_college_name_college_city: "",
        ug_college_name_college_state: "",
        diploma_college_name_college_name: "",
        diploma_college_name_college_city: "",
        diploma_college_name_college_state: "",
    }

    // Degree and branch collect one typed value when "Others" is picked. Kept separate from
    // otherCollegeData, which holds the three college fields, so neither store mixes the two.
    othersInputFields = ["pg_degree", "ug_degree", "diploma_degree", "pg_specialization", "ug_specialization", "diploma_specialization"];

    otherValueData: { [fieldValue: string]: string } = {};

    getOtherValue(fieldValue: string): string {
        return this.otherValueData[fieldValue] || "";
    }

    changeOtherValue(event: any, model: FormModel) {
        if (this.editMode()) { this.formDirty.set(true); }
        this.otherValueData[model.fieldValue] = event.target.value;
        this.checkValid(true, model);
    }

    needToShowOtherValue(fieldValue: string, fieldAnswer: any): boolean {
        if (!fieldAnswer || !this.othersInputFields.includes(fieldValue)) {
            return false;
        }

        const answer = this.getAnswer(fieldAnswer[0]);
        return (typeof answer === "string" && answer.toLowerCase() === "others")
            || (typeof answer === "object" && answer?.value?.toLowerCase() === "others");
    }

    /**
     * Degree and branch names carry punctuation onlyAlphabetsNumbersAndSpaces rejects, such as
     * the dots and slash in "B.E/B.Tech" and the ampersand in "Electronics & Communication".
     */
    isValidOtherValue(fieldValue: string): boolean {
        const value = this.getOtherValue(fieldValue).trim();
        return value.length > 1 && /^[A-Za-z0-9&.,\-\/()\s]+$/.test(value);
    }

    bindDataCollegeName(event: any, model: FormModel) {
        if (this.editMode()) { this.formDirty.set(true); }
        this.otherCollegeData[`${model.fieldValue}_${event.field}`] = event.value;
        if(this.needToShowOtherCollegeDetails(model.fieldValue, model.answer)) {
            this.checkValid(this.isValidOtherCollegeCity(model.isMandatory, model.fieldValue) && this.isValidOtherCollegeName(model.isMandatory, model.fieldValue) && this.isValidOtherCollegeState(model.isMandatory, model.fieldValue), model);
        }
    }


    bindData(event: any, model: FormModel) {
        if (this.editMode()) { this.formDirty.set(true); }
        model.answer = [];
        switch (model.fieldType) {
            case 'checkbox':
                model.answer = event;
                break;
            case 'email': {
                model.answer = [event]
                if (model.fieldValue == 'email_id') {
                    if (!this.candidateEmail()) {
                        model.isValid = true;
                        this.emailValue.set(event);
                    }
                }

                if (model.fieldValue == 'confirm_email_id') {
                    model.isValid = true;
                    this.emailConfirmValue.set(event);
                }

                if (this.emailConfirmValue() === this.emailValue() && this.emailConfirmValue() && this.emailValue()) {
                    this.duplicateData.emailDuplicate = true;
                } else {
                    this.duplicateData.emailDuplicate = false;
                }
            }; break;
            case 'multiinput': model.answer.push(model.fieldOptions?.map((option: FieldOptions) => option.value).join(' ')); break;
            case 'mobile': model.answer = [`${event[0]}-${event[1]}`]; break;
            case 'dropdown': {
                model.answer.push(event)
            } break;
            case 'input': {
                if (model.fieldValue == 'aadhar_number') {
                    model.answer.push(Number(event))
                } else {
                    model.answer.push(event)
                }
            } break;
            case 'file':
            case 'multi-file':
                model.answer = event;
                break;
            case "multi-dropdown":
                model.answer = event;
                break;
            default: model.answer.push(event);
        }

        this.checkCanShowDependentField(model);
        this.resolveParagraphTokens();
    }

    private readonly PLACEHOLDER_FIELD_TYPES = [
        'input', 'email', 'number', 'percentage', 'mobile', 'date picker',
    ];

    resolveParagraphTokens() {
        const answers = new Map<string, string>();

        this.templateCollections().forEach((template) => {
            template.forEach((field) => {
                if (!this.PLACEHOLDER_FIELD_TYPES.includes(field.fieldType)) { return; }
                const value = (field.answer || [])
                    .filter((entry: any) => entry !== null && entry !== undefined && entry !== '')
                    .map((entry: any) => this.answerToText(entry))
                    .filter((entry: string) => entry !== '')
                    .join(', ');
                if (value) { answers.set(field.fieldValue, value); }
            });
        });

        const age = this.deriveAge(answers.get('date_of_birth'));
        if (age !== null) { answers.set('age', String(age)); }

        this.templateCollections().forEach((template) => {
            template.forEach((field) => {
                if (field.fieldType !== 'paragraph' || !field.fieldDescription) { return; }
                field.resolvedLines = this.groupSegmentsIntoLines(
                    this.buildParagraphSegments(field.fieldDescription, answers)
                );
            });
        });
    }

    private buildParagraphSegments(text: string, answers: Map<string, string>): ParagraphSegment[] {
        const segments: ParagraphSegment[] = [];
        const pattern = /\{\{\s*([^{}]*?)\s*\}\}/g;
        let lastIndex = 0;
        let match: RegExpExecArray | null;

        while ((match = pattern.exec(text)) !== null) {
            if (match.index > lastIndex) {
                segments.push({ text: text.substring(lastIndex, match.index), kind: 'text' });
            }

            const answer = answers.get(match[1]);
            segments.push(answer
                ? { text: answer, kind: 'value' }
                : { text: ' '.repeat(12), kind: 'blank' });

            lastIndex = match.index + match[0].length;
        }

        if (lastIndex < text.length) {
            segments.push({ text: text.substring(lastIndex), kind: 'text' });
        }
        return segments;
    }


    private groupSegmentsIntoLines(segments: ParagraphSegment[]): ParagraphSegment[][] {
        const lines: ParagraphSegment[][] = [];
        let current: ParagraphSegment[] = [];

        const endLine = () => {
            if (current.some((segment) => segment.text.trim() || segment.kind === 'blank')) {
                lines.push(current);
            }
            current = [];
        };

        segments.forEach((segment) => {
            if (segment.kind !== 'text' || !segment.text.includes('\n')) {
                current.push(segment);
                return;
            }

            const parts = segment.text.split('\n');
            parts.forEach((part, index) => {
                if (index > 0) { endLine(); }
                if (part) { current.push({ text: part, kind: 'text' }); }
            });
        });

        endLine();
        return lines;
    }

    private answerToText(entry: any): string {
        if (entry === null || entry === undefined) { return ''; }
        return `${entry}`.trim();
    }

    private deriveAge(dateOfBirth: string | undefined): number | null {
        if (!dateOfBirth) { return null; }
        const dob = new Date(dateOfBirth);
        if (isNaN(dob.getTime())) { return null; }

        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const monthDelta = today.getMonth() - dob.getMonth();
        if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < dob.getDate())) {
            age -= 1;
        }
        return age >= 0 ? age : null;
    }

    handleFileNotFound() {
        this.templateCollections().forEach((template) => {
            template.forEach((model) => {
                if(model.fieldType === "multi-file" || model.fieldType === "file") {
                    model.answer = [];
                    model.isValid = !model.isMandatory;
                    this.removeFiles$.next();
                }
            })
        })
    }

    checkValid(event: boolean, model: FormModel) {
        model.isValid = event;

        if (this.needToShowOtherValue(model.fieldValue, model.answer)) {
            model.isValid = this.isValidOtherValue(model.fieldValue);
        }

        if (model.fieldValue === "secondary_email_id" || model.fieldValue === "email_id") {
            this.duplicateData.secondaryEmailIdSame = false;
    
            let email_id_section: FormModel | null = null;
            this.templateCollections().forEach((te) => {
                te.forEach((se) => {
                    if (model.fieldValue === "email_id" && se.fieldValue === "secondary_email_id") {
                        email_id_section = se;
                    }
                    if (model.fieldValue === "secondary_email_id" && se.fieldValue === "email_id") {
                        email_id_section = se;
                    }
                })
            })

            if(email_id_section) {
                const modelAnswer = model.answer[0]
                const comparingAnswer = (email_id_section as any).answer?.[0];

                if(modelAnswer && comparingAnswer && modelAnswer?.trim() && comparingAnswer?.trim() && modelAnswer === comparingAnswer) {
                    model.isValid = false;
                    this.duplicateData.secondaryEmailIdSame = true;
                }
            }
        }

        if (model.fieldValue === "mobile_number" || model.fieldValue === "secondary_number") {
            this.duplicateData.secondaryMobileSame = false;
    
            let mobile_section: FormModel | null = null;
            this.templateCollections().forEach((te) => {
                te.forEach((se) => {
                    if (model.fieldValue === "secondary_number" && se.fieldValue === "mobile_number") {
                        mobile_section = se;
                    }
                    if (model.fieldValue === "mobile_number" && se.fieldValue === "secondary_number") {
                        mobile_section = se;
                    }
                })
            })

            if(mobile_section) {
                const modelAnswer = model.answer[0]
                const comparingAnswer = (mobile_section as any).answer?.[0];

                if(modelAnswer && comparingAnswer && modelAnswer?.toString().trim() && comparingAnswer?.toString().trim() && modelAnswer === comparingAnswer) {
                    model.isValid = false;
                    this.duplicateData.secondaryMobileSame = true;
                }
            }
        }
    }

    verifyOnSubmit() {
        if (this.formName() === 'Registration Form' || this.formName() === ' Registration Form' || this.formName() === 'Registration Form Non-Technical') {
            if (this.enableMobile()) {
                if (this.emailVerified() && this.mobileVerified()) {
                    this.submit();
                } else {
                    if (!this.emailVerified()) {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please verify your email address' });
                    } else if (!this.mobileVerified()) {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please verify your mobile number.' });
                    }
                }
            } else {
                if (this.emailVerified()) {
                    this.submit();
                } else {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please verify your email address' });
                }
            }
        } else {
            this.submit();
        }
    }

    getAnswer(selectedAnswer: any) {
        if (selectedAnswer && typeof selectedAnswer === "object" && !Array.isArray(selectedAnswer) && Object.keys(selectedAnswer).length) {
            return selectedAnswer["value"] || selectedAnswer
        }

        return selectedAnswer;
    }

    submit() {
        let templateModels = this.templateCollections().flat(1).filter((eachModel: FormModel) => eachModel.visible);
        const validArray = templateModels.map((model: FormModel) => model.isValid);
        const isFormValid = !validArray.some(valid => valid === false || valid === undefined);
        this.markAsTouced.next();

        if (isFormValid) {
            const getAnswerMobile = (answer: any) => {
                try {
                    let updatedAnswer = answer;

                    if(answer?.length === 2) {
                        if(answer[0] && answer[1] && !answer[1]?.toString()?.includes("-")) {
                            updatedAnswer = `${answer[0]}-${answer[1]}`
                        } else {
                            updatedAnswer = answer[0] || answer[1] || null
                        }
                    } else if (answer?.length === 1) {
                        updatedAnswer = answer[0]
                    }

                    return updatedAnswer
                } catch(err) {
                    return answer
                }
            } 

            let submitData = templateModels.reduce((acc: any, field: FormModel) => {
                // Paragraph fields are display-only and hold no answer to submit.
                if(field.fieldType === "paragraph") {
                    return acc;
                }
                if(this.othersInputFields.includes(field.fieldValue)) {
                    let answer = this.getAnswer(field.answer[0]);
                    if(answer && typeof answer === "string" && answer.toLowerCase() === "others") {
                        const typedValue = this.getOtherValue(field.fieldValue).trim();
                        answer = typedValue ? typedValue : null;
                    }
                    acc[field.fieldValue] = answer;
                } else if(["pg_college_name", "ug_college_name", "diploma_college_name"].includes(field.fieldValue)) {
                    let answer = this.getAnswer(field.answer[0]);
                    if(answer && answer.toLowerCase() === "others") {
                        if(this.otherCollegeData[`${field.fieldValue}_college_name`] && this.otherCollegeData[`${field.fieldValue}_college_city`] && this.otherCollegeData[`${field.fieldValue}_college_state`]) {
                            const collegeName = this.otherCollegeData[`${field.fieldValue}_college_name`].toUpperCase();
                            const collegeCity = this.otherCollegeData[`${field.fieldValue}_college_city`].toUpperCase();
                            const collegeState = this.otherCollegeData[`${field.fieldValue}_college_state`].toUpperCase();
                            answer = `${collegeName.trim()}, ${collegeCity.trim()}, ${collegeState.trim()}`
                        } else {
                            answer = null;
                        }
                    }
                    acc[field.fieldValue] = answer;
                } else {
                    acc[field.fieldValue] = (field.fieldType === "multi-dropdown" || field.fieldType === "checkbox" || field.fieldType === "multi-file") ? this.getAnswer(field.answer) : (Array.isArray(field.answer) ? field.fieldType === "mobile" ? getAnswerMobile(field.answer) : this.getAnswer(field.answer[0]) ?? null : this.getAnswer(field.answer) ?? null);;
                }
                return acc;
            }, {});

            const hasPrimaryCountryCode = this.fieldRemoved.includes("country_code");
            const hasSecondaryCountryCode = this.fieldRemoved.includes("secondary_num_country_code");
            if(!hasSecondaryCountryCode) {
                if(submitData["secondary_number"] && typeof submitData["secondary_number"] === "string" && submitData["secondary_number"].includes("-")) {
                    const splitted = submitData["secondary_number"].split("-");
                    submitData["secondary_number"] = splitted[splitted.length - 1]
                }
            }
            if(!hasPrimaryCountryCode) {
                if(submitData["mobile_number"] && typeof submitData["mobile_number"] === "string" && submitData["mobile_number"].includes("-")) {
                    const splitted = submitData["mobile_number"].split("-");
                    submitData["mobile_number"] = splitted[splitted.length - 1]
                }
            }

            if (this.fieldRemoved.length > 0) {
                this.fieldRemoved.forEach((field: string) => {
                    const mobileNumber = field === "country_code" ? (submitData["mobile_number"] || submitData["parents_mobile_number"]) : submitData["secondary_number"];
                    const selectedField = field === "country_code" ? (submitData["mobile_number"] ? "mobile_number" : "parents_mobile_number") : (submitData["secondary_number"] ? "secondary_number" : (submitData["mobile_number"] ? "mobile_number" : "parents_mobile_number"));
                    let fieldValues;
                    if (mobileNumber?.includes('-')) {
                        fieldValues = mobileNumber.split('-');
                        if(fieldValues.length === 2) {
                            submitData = {
                                ...submitData,
                                [field]: fieldValues[0],
                                [selectedField]: fieldValues[1]
                            };
                        } else {
                            submitData = {
                                ...submitData,
                                [field]: `${fieldValues[0]}-${fieldValues[1]}`,
                                [selectedField]: fieldValues[2]
                            };
                        }
                    } else {
                        fieldValues = ['91', mobileNumber];
                        submitData = {
                            ...submitData,
                            [field]: fieldValues[0]
                        };
                    }
                });
            }

            const payload = {
                ...submitData,
            };

            if(!this.fieldRemoved.length) {
                Object.keys(payload).forEach((key) => {
                    if(["mobile_number", "parents_mobile_number", "secondary_number"].includes(key)) {
                        const value = payload[key];
                        if(value && typeof value === "string" && value.includes("-")) {
                            const splited = value.split("-");
                            if(splited.length) {
                                payload[key] = splited[splited.length - 1]
                            }
                        }
                    }
                })
            }

            this.submittingForm.set(true);
            if (this.formType() === 'registration') {
                if (this.editMode() && this.registrationId()) {
                    const editPayload = {
                        ...payload,
                        candidate_uuid: this.candidateUUID()
                    };
                    this.formBuilderService.UpdateFormFastApi(editPayload, this.eventId(), 'registration', parseInt(this.registrationId()))
                        .subscribe({
                            next: (response) => {
                                this.submittedSuccessfully.set(true);
                                this.submittingForm.set(false);
                            },
                            error: (error) => {
                                this.submittingForm.set(false);
                                this.handleFormSubmissionError(error);
                            }
                        });
                } else {
                    this.formBuilderService.submitRegistrationFormFastApi(payload, this.eventId())
                        .subscribe({
                            next: (response) => {
                                this.submittedSuccessfully.set(true);
                                this.submittingForm.set(false);
                                this.triggerSkillScoring(this.eventId());
                            },
                            error: (error) => {
                                this.submittingForm.set(false);
                                this.handleFormSubmissionError(error);
                            }
                        });
                }
            } else if (this.formType() === 'documentation') {
                this.formBuilderService.submitDocumentationFormFastApi(payload, this.stageId())
                    .subscribe({
                        next: (response) => {
                            this.submittedSuccessfully.set(true);
                            this.submittingForm.set(false);
                        },
                        error: (error) => {
                            this.submittingForm.set(false);
                            this.handleFormSubmissionError(error);
                        }
                    });
            }
            else if (this.formType() === 'offer') {
                this.formBuilderService.submitOfferFormFastApi(payload, this.eventId(), this.stageId())
                    .subscribe({
                        next: (response) => {
                            this.offerFormSuccess.set(true);
                            this.submittingForm.set(false);
                        },
                        error: (error) => {
                            this.submittingForm.set(false);
                            this.handleFormSubmissionError(error);
                        }
                    });
            } else {
                this.submittingForm.set(false);
            }
        } else {
            this.messageService.add({
                severity: 'warn',
                summary: 'Missing Required Fields',
                detail: 'Please complete all required fields before submitting the form.'
            });
        }
    }

  closeWindow(action: string) {
    if (action === 'success') {
      window.close();
    }
    else {
      // this.emailExists = false;
      // this.mobileExists = false;
    }
  }

  private handleFormSubmissionError(error: any) {
    if (error?.status === 403 && error?.error?.detail === 'Session has expired') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Session Expired',
        detail: 'Your session has expired.'
      });
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      return;
    }
    if(error?.error?.detail?.toLowerCase?.()?.includes("file not found")) {
      this.handleFileNotFound();
    }
    const isSecurityCheck = error?.error?.detail?.toLowerCase?.()?.includes('security check');
    const detail = error?.error?.detail;
    const safeDetail = (Array.isArray(detail) ?
        detail.map((err: any) => err?.msg).join(', ') :
        detail) || 'An error occurred while submitting the form';
    this.messageService.add({
      severity: (error?.status === 422 || isSecurityCheck) ? 'warn' : 'error',
      summary:  (error?.status === 422 || isSecurityCheck) ? 'Warning' : 'Error',
      detail: safeDetail
    });
  }

  private async loadLookupData(): Promise<any> {
    return this.loadLookupDataWithFormData(this.formData);
  }

  private async loadLookupDataWithFormData(formData: any): Promise<any> {
    if (!formData?.form_fields) {
      return {
        roles: [],
        institutes: [],
        certifications: [],
        languages: [],
        skills: [],
        ssc_boards: [],
        intermediate_boards: [],
        entrance_tests: [],
        education_degrees: [],
        education_branches: []
      };
    }

    const context = this.formType() === 'registration' ? 'form' : undefined;
    return await this.fieldLookupService.loadOptimizedLookupData(this.eventId(), formData.form_fields, context);
  }

  private loadLookupDataInBackground(): void {
    if (!this.formData?.form_fields) return;
    
    const context = this.formType() === 'registration' ? 'form' : undefined;
    this.fieldLookupService.loadOptimizedLookupData(this.eventId(), this.formData.form_fields, context)
      .then((lookupData) => {
        this.updateLookupData(lookupData);
        this.transformFormFields(this.formData);
        
        if(!this.previewMode && this.formType() === 'registration') {
            if(lookupData.roles?.length) {
                lookupData.roles[0].isOpened = true;
            }

            this.eventDetails.set({job_roles: lookupData.roles || []})
            this.loadingJobDetails.set(false)
        }
      })
      .catch((error) => {
        this.loadingJobDetails.set(false)
      });
  }

  private updateLookupData(lookupData: any): void {
    const captureConfig = lookupData?.proctoring_config;
    this.photoCaptureMode = captureConfig?.enabled ? (captureConfig.mode ?? null) : null;
    if (lookupData?.roles) {
      const roles: JobRole[] = lookupData.roles;
      const uniqueLocations = new Set<string>();
      const jobRoles: any[] = [];

      roles.forEach((role) => {
        role.preferred_location?.forEach((location) => {
          uniqueLocations.add(location);
        });
        jobRoles.push({
          label: role.job_role_name,
          value: role.job_role_id
        });
      });

      this.jobRoles.set(jobRoles);
      this.preferredLocations.set(Array.from(uniqueLocations).map(location => ({
        label: location,
        value: location
      })));
    }

    (lookupData?.institutes?.items || []).forEach((institution: any) => {
        if(typeof institution.name === "string" && institution.name?.toLowerCase() === "others") {
            institution.city = "";
            institution.state = "";
        }
    })

    this.instituteList.set(lookupData?.institutes || []);
    this.certificateList.set(lookupData?.certifications || []);
    this.languagesList.set(lookupData?.languages || []);
    this.skillsList.set(lookupData?.skills || []);
    this.sscBoardsList.set(lookupData?.ssc_boards || []);
    this.intermediateBoardsList.set(lookupData?.intermediate_boards || []);
    this.entranceTestsList.set(lookupData?.entrance_tests || []);
    this.educationDegreesList.set(lookupData?.education_degrees || []);
    this.educationBranchesList.set(lookupData?.education_branches || []);
  }

    needToShowOtherCollegeDetails(fieldValue: string, fieldAnswer: any) {
        if (!fieldAnswer) {
            return false;
        }

        const answer = this.getAnswer(fieldAnswer[0]);
        let isOthers = false;

        if (typeof answer === "string" && answer.toLowerCase() === "others") {
            isOthers = true;
        }

        if (typeof answer === "object" && answer?.value?.toLowerCase() === "others") {
            isOthers = true;
        }

        return ["pg_college_name", "ug_college_name", "diploma_college_name"].includes(fieldValue) && isOthers
    }

    otherCollegeDetails(model: FormModel, field: string) {
      this.bindDataCollegeName({ field: field, value: this.otherCollegeData[`${model.fieldValue}_${field}`] }, model);
    }

    onlyAlphabetsNumbersAndSpaces(value: string): boolean {
        return /^[A-Za-z0-9\s]+$/.test(value.trim());
    }

    isValidOtherCollegeName(isMandatory: boolean, fieldValue: string) {
        return (this.onlyAlphabetsNumbersAndSpaces(this.otherCollegeData[`${fieldValue}_college_name`]) && this.otherCollegeData[`${fieldValue}_college_name`].trim().length > 2)
    }

    isValidOtherCollegeState(isMandatory: boolean, fieldValue: string) {
        return (this.onlyAlphabetsNumbersAndSpaces(this.otherCollegeData[`${fieldValue}_college_state`]) && this.otherCollegeData[`${fieldValue}_college_state`].trim().length > 2)
    }

    isValidOtherCollegeCity(isMandatory: boolean, fieldValue: string) {
        return (this.onlyAlphabetsNumbersAndSpaces(this.otherCollegeData[`${fieldValue}_college_city`]) && this.otherCollegeData[`${fieldValue}_college_city`].trim().length > 2);
    }

    getOtherCollegeValue(fieldValue: string, field: string) {
        return this.otherCollegeData[`${fieldValue}_${field}`];
    }

    changeOtherCollegeValue(event: any, fieldValue: string, field: string, formModel: FormModel) {
        this.otherCollegeData[`${fieldValue}_${field}`] = event.target.value;
        this.checkValid(this.isValidOtherCollegeCity(formModel.isMandatory, fieldValue) && this.isValidOtherCollegeName(formModel.isMandatory, fieldValue) && this.isValidOtherCollegeState(formModel.isMandatory, fieldValue), formModel);
    }
  private checkSkillScoreEnabled(): boolean {
    const companyDetails = this.companyMetadata();
    
    if (!companyDetails?.company_config) {
      return false;
    }
    
    return companyDetails.company_config.skill_scores ?? false;
  }

  private hasResumeField(): boolean {
    if (!this.formData?.form_fields) {
      return false;
    }
    
    return this.formData.form_fields.some((section: any) => 
      section.section_form_fields?.some((field: any) => 
        field.name === 'resume_cv' && field.type === 'File'
      )
    );
  }

  private triggerSkillScoring(eventUuid: string): void {
    if (!this.checkSkillScoreEnabled() || !this.hasResumeField()) {
      return;
    }

    this.formBuilderService.submitCandidateSkillScoreFastApi(eventUuid)
      .subscribe({
        next: (response) => {
        },
        error: (error) => {
          console.error('Error initiating skill scoring:', error);
        }
      });
  }

    getDependenciesFields() {
        const allDependencies: Set<string> = new Set();
        this.templateCollections()?.forEach((section) => {
            section?.forEach((field) => {
                const dependencies = field.dependencies || [];
                dependencies.forEach((dependency) => {
                    dependency.show_fields.forEach((field) => {
                        allDependencies.add(field);
                    })
                })
            })
        })
        return allDependencies;
    }

    getAllFieldsMap(): Map<string, FormModel> {
        const map = new Map<string, FormModel>();
        this.templateCollections()?.forEach(section => {
            section?.forEach(field => {
                map.set(field.fieldValue, field);
            });
        });
        return map;
    }

    updateDependentField() {
        this.dependenciesFields = this.getDependenciesFields();
        this.fieldsMap = this.getAllFieldsMap();
            
        this.templateCollections()?.forEach((section) => {
            section?.forEach((field) => {
                if (this.dependenciesFields.has(field.fieldValue)) {
                    field.visible = false;
                    field.isMandatory = false;
                    field.isValid = true;
                }
            })
        })
    }

    /**
     * Computes the global visibility for all dependent fields across all parent fields.
     * This ensures proper handling when multiple fields control the same dependent field.
     */
    private computeGlobalFieldVisibility(): Map<string, boolean> {
        const globalVisibility = new Map<string, boolean>();

        // First pass: collect all dependent fields and initialize them as hidden
        this.dependenciesFields.forEach(fieldId => {
            globalVisibility.set(fieldId, false);
        });

        // Second pass: check all parent fields and their dependencies
        this.templateCollections()?.forEach((section) => {
            section?.forEach((field) => {
                if (!["dropdown", "radio", "checkbox", "multi-dropdown"].includes(field.fieldType)) return;
                if (!field.dependencies || field.dependencies.length === 0) return;

                const selectedOption = ["checkbox", "multi-dropdown"].includes(field.fieldType)
                    ? field.answer
                    : (field.answer?.[0]?.value || field.answer[0] || "");

                field.dependencies.forEach(dep => {
                    const matches = Array.isArray(selectedOption)
                        ? selectedOption.some((opt: any) => {
                            const optValue = typeof opt === 'object' ? opt.value : opt;
                            return optValue === dep.option;
                        })
                        : dep.option === selectedOption;

                    if (matches) {
                        // If any parent shows a field, mark it as visible (OR logic)
                        (dep.show_fields || []).forEach(fid => {
                            globalVisibility.set(fid, true);
                        });
                    }
                });
            });
        });

        return globalVisibility;
    }

    /**
     * Updates visibility of dependent fields based on the selected dropdown option.
     * Handles multi-level (nested) dependencies recursively.
     */
    checkCanShowDependentField(model: FormModel, visited = new Set<string>()) {
        // Only handle dropdown-type fields for now
        if (!["dropdown", "radio", "checkbox", "multi-dropdown"].includes(model.fieldType)) return;

        // Compute global visibility for all fields
        const globalVisibility = this.computeGlobalFieldVisibility();

        // Apply visibility to all dependent fields
        globalVisibility.forEach((shouldShow, fieldId) => {
            const field = this.fieldsMap.get(fieldId);
            if (!field) return;

            // Prevent infinite recursion in circular dependencies
            if (visited.has(fieldId)) return;

            if (shouldShow) {
                // Show Field
                if (!field.visible) {
                    field.visible = true;
                    field.isMandatory = field.is_required ? true : false;
                    field.isValid = this.previewMode ? true : !field.is_required;
                }

                // Mark visited before recursive check
                visited.add(fieldId);

                // Recursively check nested dependencies for this field
                if (field.dependencies && field.dependencies.length) {
                    this.checkCanShowDependentField(field, visited);
                }

                // Remove from visited after recursion completes
                visited.delete(fieldId);

            } else {
                // Hide field only if no other parent field wants it visible
                if (field.visible || (field.answer && field.answer.length)) {
                    field.visible = false;
                    field.answer = []; // clear value
                    field.isValid = true;

                    // Recursively hide its child dependents
                    this.hideNestedDependentFields(field, this.fieldsMap);
                }
            }
        });
    }

    /**
     * Recursively hides all dependent fields (and their children)
     * when a parent field is hidden.
     */
    hideNestedDependentFields(field: FormModel, allFieldsMap: Map<string, FormModel>, visited = new Set<string>()) {
        // Avoid re-processing the same field in circular dependencies
        if (!field?.dependencies || visited.has(field.fieldId)) return;

        visited.add(field.fieldId);

        field.dependencies.forEach(dep => {
            (dep.show_fields || []).forEach(depFieldId => {
                const depField = allFieldsMap.get(depFieldId);
                if (depField) {
                    depField.visible = false;
                    depField.answer = []; // clear hidden field value
                    depField.isValid = true;
                    // Recursively hide deeper nested dependents
                    this.hideNestedDependentFields(depField, allFieldsMap, visited);
                }
            });
        });

        visited.delete(field.fieldId);
    }


    canShowSection(section: FormModel[]) {
        return section.some((field) => field.visible === true)
    }
}